import React from "react";
import { motion } from "framer-motion";
import SEO from "../../components/SEO";
import { organizationSchema, breadcrumbSchema, articleSchema, SITE_PUBLISHED, SITE_MODIFIED } from "../../utils/schemaOrg";
import {
  Wifi,
  Video,
  Bell,
  Mail,
  Printer,
  Coffee,
  Gamepad2,
  Phone,
  ShieldCheck,
  Brush,
  Smartphone,
  Gift,
  Wind,
  ChevronRight,
  Sparkles,
  Users,
  Circle,
  MapPin,
  Building2,
  Leaf,
  Snowflake,
} from "lucide-react";

import HiveWorkspaces from "./HiveWorkspaces";
import RetrofuturismRoadmap from "./RetrofuturismRoadmap";
import { useOutletContext, useNavigate } from "react-router-dom";

export default function AboutUs() {
  const { theme } = useOutletContext();
  const navigate = useNavigate();

  const amenities = [
    { label: "High-speed internet & enterprise-grade connectivity", icon: Wifi },
    {
      label: "Meeting & conference rooms with video conferencing",
      icon: Video,
    },
    { label: "Reception & concierge services", icon: Bell },
    { label: "Mail & package handling", icon: Mail },
    { label: "Printing, scanning & photocopying", icon: Printer },
    { label: "Fully stocked pantry with complimentary coffee & tea", icon: Coffee },
    { label: "Game lounge & recreational spaces", icon: Gamepad2 },
    { label: "Private phone booths", icon: Phone },
    { label: "24/7 access with multi-level security", icon: ShieldCheck },
    { label: "Community app for seamless service requests", icon: Smartphone },
    { label: "Exclusive partner offers & community events", icon: Gift },
    {
      label: "Ergonomic furniture, air conditioning & power backup",
      icon: Snowflake,
    },
  ];

  const isDark = theme === "dark";

  return (
    <div
      className={`w-full pt-8 overflow-hidden ${isDark ? "bg-black text-white" : "bg-white text-gray-900"
        }`}
    >
      <SEO
        title="About Us – India's Premium Coworking Space Provider"
        description="Learn about The Hive Workspaces – India's leading coworking and managed office space provider. Present across Chennai, Bangalore, Hyderabad, Pune, Mumbai and Delhi. World-class amenities, flexible plans and enterprise solutions."
        canonical="/about-us"
        ogImage="/CB_26495.jpg"
        structuredData={[
          {
            "@type": "AboutPage",
            "@id": "https://www.hiveworkspaces.com/about-us#aboutpage",
            name: "About The Hive Workspaces",
            url: "https://www.hiveworkspaces.com/about-us",
            isPartOf: { "@id": "https://www.hiveworkspaces.com/#website" },
            about: { "@id": "https://www.hiveworkspaces.com/#organization" },
            datePublished: SITE_PUBLISHED,
            dateModified: SITE_MODIFIED,
            mainEntity: organizationSchema(),
            inLanguage: "en-IN",
          },
          articleSchema({
            headline: "About The Hive Workspaces",
            description: "Learn about The Hive Workspaces – premium coworking and managed offices across India.",
            url: "https://www.hiveworkspaces.com/about-us",
            datePublished: SITE_PUBLISHED,
            dateModified: SITE_MODIFIED,
            authorName: "",
            authorJobTitle: "",
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://www.hiveworkspaces.com/" },
            { name: "About Us", url: "https://www.hiveworkspaces.com/about-us" },
          ]),
        ]}
      />
      {/* Hero Section - Elegant Card */}
      <section
        className={`py-3 bg-gradient-to-b ${isDark ? "from-black to-slate-900" : "from-white to-gray-50"
          }`}
      >
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-6">
                <span
                  className={`text-xs font-medium  uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                    }`}
                >
                  Designed for Modern Businesses
                </span>
              </div>

              <div className="mb-8">
                <h1 className="text-5xl md:text-6xl font-medium mb-4 liber">
                  The Hive Workspaces

                </h1>
                
                <div
                  className={`w-20 h-0.5 mb-6 ${isDark ? "bg-slate-600" : "bg-gray-300"
                    }`}
                ></div>
              </div>

              <div
                className={`rounded-xl p-8 shadow-sm border mb-8 ${isDark
                  ? "bg-black/60 border-slate-800"
                  : "bg-white border-gray-100"
                  }`}
              >
                <p
                  className={`mb-6 leading-relaxed ${isDark ? "text-slate-200" : "text-gray-700"
                    }`}
                >
                  The Hive Workspaces is a leading provider of premium Coworking and managed
                  office spaces in India, offering flexible, fully serviced and
                  technology-enabled environments for ambitious businesses.
                </p>
                <p
                  className={`text-sm leading-7 ${isDark ? "text-slate-200" : "text-gray-600"
                    }`}
                >
                  With a growing presence across major commercial districts, The Hive delivers
                  a seamless mix of Grade-A infrastructure, hospitality-driven service, and a
                  collaborative community, creating inspiring workspaces designed to support
                  businesses of every size.
                </p>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <button
                  className={`px-5 py-3 font-medium rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex items-center gap-2 text-sm ${isDark ? "bg-white text-black" : "bg-black text-white"
                    }`}
                  onClick={() => { navigate("/solutions") }}
                >
                  <span>Explore Solutions</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </motion.div>
            </div>

            <div className="relative">
              <div
                className={`rounded-2xl shadow-lg overflow-hidden border ${isDark ? "bg-black border-slate-800" : "bg-white border-gray-100"
                  }`}
              >
                <div className="aspect-[4/3] relative">
                  <img
                    src="/CB_26495-min.jpg"
                    alt="The Hive Workspace interior"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3
                        className={`font-medium mb-1 ${isDark ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Premium Managed Workspaces
                      </h3>
                      <p
                        className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"
                          }`}
                      >
                        Grade-A Infrastructure • Hospitality-Driven •
                        Collaborative
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are - Minimalist */}

      {/* <section className="py-3">
        <div className="max-w-7xl mx-auto px-5">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className={`relative rounded-3xl border backdrop-blur-sm p-8 md:p-10  ${isDark
              ? "border-slate-800 bg-black/40"
              : "border-gray-400 bg-white/70"
              }`}
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`w-10 h-0.5 ${isDark ? "bg-white" : "bg-black"}`}
                ></div>
                <h2 className="text-3xl md:text-4xl  liber">
                  Who We Are
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <p
                    className={`flex  items-start leading-relaxed text-sm md:text-base ${isDark ? "text-slate-200" : "text-gray-700"
                      }`}
                  >
                    <Circle size={16} className="mr-2 pt-2" />The Hive places workspaces within lifestyle destinations, seamlessly combining work, leisure and community experiences.
                  </p>
                  <p
                    className={`flex  items-start leading-relaxed text-sm md:text-base ${isDark ? "text-slate-200" : "text-gray-700"
                      }`}
                  >
                    <Circle size={16} className="mr-2 pt-2" /> Built on its signature Work-Play-Grow approach, The Hive supports enterprises, SMEs, startups and entrepreneurs alike.
                  </p>
                </div>
                <div className="space-y-4">
                  <p
                    className={`flex  items-start leading-relaxed text-sm md:text-base ${isDark ? "text-slate-200" : "text-gray-700"
                      }`}
                  >
                    <Circle size={16} className="mr-2 pt-2" /> Members enjoy direct access to retail, hospitality, entertainmentand food & beverage offerings within the same ecosystem.
                  </p>
                  <p
                    className={`flex  items-start leading-relaxed text-sm md:text-base ${isDark ? "text-slate-200" : "text-gray-700"
                      }`}
                  >
                    <Circle size={16} className="mr-2 pt-2" /> By embedding offices in vibrant community hubs rather than standalone buildings, The Hive redefines the coworking experience.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}


      <section
        className={`py-3 bg-gradient-to-b ${isDark ? "from-black via-slate-900 to-black" : "from-white to-gray-50"
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 space-y-12">

          {/* -----------------------------------------
       TOP SECTION — MISSION
    ------------------------------------------ */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-3"
          >
            {/* Mission Label */}
            {/* <div className="flex items-center gap-3">
              <div className={`w-12 h-0.5 ${isDark ? "bg-white" : "bg-black"}`}></div>
              <span
                className={`text-sm font-medium uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                  }`}
              >
                Our Mission
              </span>
            </div> */}

            <div className="flex justify-center mb-4 mt-4 ">
              <div className="inline-flex items-center gap-2">
                <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
                <span
                  className={`text-xs font-medium  uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                    }`}
                >
                  Our Mission
                </span>
                <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
              </div>
            </div>

            {/* Heading */}
            <h2 className="text-4xl text-center md:text-5xl liber leading-tight">
              Driving Business Excellence Forward

            </h2>

            {/* Mission Card */}
            <div className="relative">
              <div
                className={`absolute -inset-4 rounded-2xl blur-xl ${isDark
                  ? "bg-gradient-to-r from-white/5 to-transparent"
                  : "bg-gradient-to-r from-black/5 to-transparent"
                  }`}
              ></div>

              <div
                className={`relative p-10 rounded-2xl bg-gradient-to-br ${isDark ? "from-white/10 to-slate-800" : "from-black to-gray-800"
                  } text-white`}
              >
                <div className="text-5xl mb-1">"</div>

                <p className="text-lg leading-relaxed mb-3">
                  To deliver thoughtfully designed, service-driven workspaces that combine
                  flexibility, hospitality, and efficiency, redefining how modern companies
                  experience the office.
                </p>

                <div className="flex items-center justify-between">
                  <div></div>
                  <div className="flex items-center justify-end gap-3">
                    <div className="w-8 h-0.5 bg-white/50"></div>
                    <span className="text-sm font-medium">The Hive Promise</span>
                    <div className="text-5xl">"</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* -----------------------------------------
       BOTTOM SECTION — WHY CHOOSE US
    ------------------------------------------ */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-2"
          >
            {/* Label */}
            {/* <div className="flex items-center gap-3">
              <div className={`w-12 h-0.5 ${isDark ? "bg-white" : "bg-black"}`}></div>
              <span
                className={`text-sm font-medium uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                  }`}
              >
                Why Choose Us
              </span>
            </div> */}

            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2">
                <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
                <span
                  className={`text-xs font-medium  uppercase ${isDark ? "text-slate-300" : "text-gray-500"
                    }`}
                >
                  Why Choose Us
                </span>
                <div className={`w-4 h-px ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
              </div>
            </div>

            {/* Heading */}
            <h3 className="text-3xl text-center liber leading-tight">
              The Hive
              <span className={isDark ? "text-slate-400 pl-2" : "text-gray-400 pl-2"}>
                Advantage
              </span>
            </h3>

            {/* List Items */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4 ">
              {[
                {
                  icon: Sparkles,
                  title: "Customized Solutions",
                  description:
                    "Private offices, corporate suites, managed floors, or fully custom build-to-suit workspaces."
                },
                {
                  icon: MapPin,
                  title: "Prime Strategic Locations",
                  description:
                    "Centres placed across high-demand commercial hubs for maximum accessibility."
                },
                {
                  icon: Building2,
                  title: "Hospitality-Led Experience",
                  description:
                    "Concierge-level support, seamless facilities and frictionless operations."
                },
                {
                  icon: Users,
                  title: "Thriving Business Community",
                  description:
                    "A curated network of founders, innovators and professionals with events and workshops."
                },
                {
                  icon: Leaf,
                  title: "Sustainable & Smart",
                  description:
                    "Eco-friendly, energy-efficient and technology-integrated workspace environment."
                },
                {
                  icon: ShieldCheck,
                  title: "Enterprise-Grade Security",
                  description:
                    "24/7 access control, surveillance, and two-level security protocols for complete peace of mind."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className={`group flex gap-6 p-6 rounded-2xl transition-all duration-300
        cursor-default
        hover:shadow-xl
        ${isDark ? "bg-gray-900 hover:bg-gray-800" : "hover:bg-white bg-gray-100"}`}
                >
                  {/* Icon */}
                  <div
                    className={`w-10 h-10 px-2 rounded-xl flex items-center justify-center
        ${isDark
                        ? "bg-white/10 group-hover:bg-white/20"
                        : "bg-gray-200 group-hover:bg-black group-hover:text-white"
                      }`}
                  >
                    <item.icon className={`w-4 h-4 ${isDark ? "text-white" : ""}`} />
                  </div>

                  {/* Text */}
                  <div>
                    <h4
                      className={`text-lg mb-1 ${isDark ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {item.title}
                    </h4>

                    <p
                      className={`leading-relaxed ${isDark ? "text-slate-300" : "text-gray-600"
                        }`}
                    >
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

          </motion.div>
        </div>
      </section>

      <RetrofuturismRoadmap />

      {/* Premium Amenities - Grid Layout */}
      <section className="py-3">
        <div className="max-w-7xl mx-auto px-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-10">

              <h2 className="text-3xl md:text-4xl  mt-4 mb-2 liber">
                Premium Amenities
              </h2>
              <p
                className={`max-w-2xl mx-auto text-sm md:text-base ${isDark ? "text-slate-300" : "text-gray-600"
                  }`}
              >
                Comprehensive amenities designed for the way modern businesses work
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {amenities.map((a, idx) => {
                const Icon = a.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    whileHover={{
                      scale: 1.02,
                      zIndex: 1,
                      transition: { duration: 0.2 },
                    }}
                    className="group relative"
                  >
                    {/* <div
                      className={`absolute inset-0 border rounded-xl transition-colors z-20 pointer-events-none ${isDark
                        ? "border-slate-800 group-hover:border-white/40"
                        : "border-gray-100 group-hover:border-black/30"
                        }`}
                    ></div> */}
                    <div
                      className={`relative p-5 rounded-xl backdrop-blur-sm transition-all duration-300 ${isDark
                        ? "bg-black/40 group-hover:bg-white/10"
                        : "bg-white/80 group-hover:bg-black/5"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2.5 rounded-lg transition-colors ${isDark
                            ? "bg-white/10 group-hover:bg-white/20"
                            : "bg-black/5 group-hover:bg-black/10"
                            }`}
                        >
                          <Icon
                            className={`w-5 h-5 ${isDark ? "text-white" : "text-black"
                              }`}
                          />
                        </div>
                        <p
                          className={`leading-relaxed text-sm md:text-base font-medium transition-colors ${isDark
                            ? "text-slate-300 group-hover:text-white"
                            : "text-gray-700 group-hover:text-gray-900"
                            }`}
                        >
                          {a.label}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* <Members /> */}
      <HiveWorkspaces />
    </div>
  );
}
